import { Box, MenuItem, TextField } from '@mui/material';
import { useState } from 'react';
import { CourseDetailEntity, CourseEntity, CoursePayload } from 'src/entities/Course.entity';

import { IdOption, nextTripOptions, tripLabel, withCurrentOption } from './courseMasterUtils';
import { useCourseOptions } from './useCourseOptions';

type Props = {
  course: CourseDetailEntity; courses: CourseEntity[]; disabled: boolean; onChange: (data: CoursePayload) => void;
  /** 入力欄（出庫・帰庫の時刻）の保存。失敗（false）なら欄をサーバの値に戻す */
  onSaveText: (data: CoursePayload) => Promise<boolean>;
  onUnlink: () => void;
};
type DepotField = 'depot_departure_start_time' | 'depot_departure_end_time' | 'depot_arrival_start_time' | 'depot_arrival_end_time';
type DepotProp = 'depotDepartureStartTime' | 'depotDepartureEndTime' | 'depotArrivalStartTime' | 'depotArrivalEndTime';
type DepotSide = { label: string; start: { key: DepotField; prop: DepotProp }; end: { key: DepotField; prop: DepotProp } };
// 出庫・帰庫は欄を 1 つにして、拠点の到着（start）と出発（end）に同じ値を書く。
// Why not 到着・出発の 2 欄: 本番の出庫・帰庫の運行は到着と出発がいつも同じ（BigQuery 2026-10-21、出庫・帰庫とも 約 22 万件で差 0）。
// API・DB の 4 列はそのまま（呼び出し・ダイヤ作成が 4 列で読み書きする）
const DEPOT: DepotSide[] = [
  { label: '出庫時刻', start: { key: 'depot_departure_start_time', prop: 'depotDepartureStartTime' }, end: { key: 'depot_departure_end_time', prop: 'depotDepartureEndTime' } },
  { label: '帰庫時刻', start: { key: 'depot_arrival_start_time', prop: 'depotArrivalStartTime' }, end: { key: 'depot_arrival_end_time', prop: 'depotArrivalEndTime' } },
];
const toId = (value: string) => (value === '' ? null : Number(value));
const firstTripLabel = (c: CourseEntity) => `${c.code}${c.name ? ` ${c.name}` : ''}`;
// 出庫・帰庫の時刻は選択欄と別の行に置き、紐付け先の欄（2 列ぶん）が出ても 1 行にそろえる。狭い画面は 2 列で折り返す
const GRID = { display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 } as const;

/**
 * 基本情報は項目ごとに即保存（判断事項 3）。便を 2 以上にするときは紐付け先を選んだ時点で trip_no と一緒に送る（API は 2 便以降で紐付け先必須）。
 * ダイヤに入っている 2 便を 1 便に戻すときは onUnlink（車両を選ぶダイアログ）に委ねる（判断事項 13）
 */
export default function CourseBasicFields({ course, courses, disabled, onChange, onSaveText, onUnlink }: Props) {
  const { trucks, drivers, partners } = useCourseOptions();
  const [pendingTripNo, setPendingTripNo] = useState<number | null>(null);
  const truckOptions = withCurrentOption(trucks, course.defaultTruck && { id: course.defaultTruck.id, label: course.defaultTruck.licensePlateValue });
  const driverOptions = withCurrentOption(drivers, course.defaultDriver && { id: course.defaultDriver.id, label: course.defaultDriver.name });
  const partnerOptions = withCurrentOption(partners, course.partnerCompany && { id: course.partnerCompany.id, label: course.partnerCompany.name });
  // 紐付け先は deleted でない 1 便すべて（API 仕様）。今の紐付け先は一覧に無くても残す
  const firstTrips = courses.filter((c) => c.tripNo === 1 && c.id !== course.id);
  const currentFirst = course.firstTripCourse;
  const firstTripOptions: IdOption[] = firstTrips.map((c) => ({ id: c.id, label: firstTripLabel(c) }));
  // Why not withCurrentOption: 車両・ドライバー用で「（無効）」を付ける。コースの状態は画面に出さない（#379）
  if (currentFirst && !firstTripOptions.some((o) => o.id === currentFirst.id)) firstTripOptions.unshift({ id: currentFirst.id, label: currentFirst.code });
  const showFirstTrip = course.tripNo >= 2 || pendingTripNo != null;
  const select = (label: string, value: number | '', options: IdOption[], key: 'default_truck_id' | 'default_driver_id' | 'partner_company_id') => (
    <TextField select size="small" label={label} value={value} disabled={disabled} onChange={(e) => onChange({ [key]: toId(e.target.value) })}>
      <MenuItem value="">未設定</MenuItem>
      {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
    </TextField>
  );
  const onTripChange = (tripNo: number) => {
    if (tripNo === 1) {
      setPendingTripNo(null);
      // 1 便のまま 2便を選んで紐付け先を選ばずに戻しただけなら、保留を消すだけで送らない
      if (course.tripNo === 1) return;
      if (course.firstTripCourse && course.timetables.length > 0) onUnlink(); else onChange({ trip_no: 1 });
      return;
    }
    if (course.firstTripCourse) { setPendingTripNo(null); onChange({ trip_no: tripNo }); return; }
    setPendingTripNo(tripNo); // 紐付け先を選んでから送る
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
      <Box sx={GRID}>
        {select('通常運送会社', course.partnerCompany?.id ?? '', partnerOptions, 'partner_company_id')}
        {select('通常車両', course.defaultTruck?.id ?? '', truckOptions, 'default_truck_id')}
        {select('通常ドライバー', course.defaultDriver?.id ?? '', driverOptions, 'default_driver_id')}
        <TextField
          select
          size="small"
          label="便区分"
          value={pendingTripNo ?? course.tripNo}
          disabled={disabled}
          onChange={(e) => onTripChange(Number(e.target.value))}
          helperText={pendingTripNo != null ? '紐付け先の 1 便を選ぶと保存します' : undefined}
        >
          {nextTripOptions(courses).map((n) => <MenuItem key={n} value={n}>{tripLabel(n)}</MenuItem>)}
        </TextField>
        {showFirstTrip && (
          <TextField
            select
            size="small"
            label="紐付け先の1便コース"
            value={course.firstTripCourse?.id ?? ''}
            disabled={disabled}
            sx={{ gridColumn: { lg: 'span 2' } }}
            onChange={(e) => {
              const id = Number(e.target.value);
              setPendingTripNo(null);
              onChange(pendingTripNo != null ? { trip_no: pendingTripNo, first_trip_course_id: id } : { first_trip_course_id: id });
            }}
          >
            {firstTripOptions.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
          </TextField>
        )}
      </Box>
      <Box sx={GRID} data-testid="course-depot-times">
        {DEPOT.map(({ label, start, end }) => {
          // 到着と出発が違う（4 列のころに入れた）ときは到着を出す。保存すると両方がその値になる
          const server = course[start.prop] ?? course[end.prop] ?? null;
          return (
            <TextField
              key={`${start.key}-${server ?? ''}`}
              type="time"
              size="small"
              label={label}
              defaultValue={server ?? ''}
              disabled={disabled}
              slotProps={{ inputLabel: { shrink: true } }}
              onBlur={(e) => {
                const input = e.target;
                const next = input.value || null;
                // 表示している値と比べる。到着と出発が違うコースで、欄に触れただけで両方を書き換えないため
                if (next === server) return;
                // Why not 失敗を放っておく: defaultValue の欄はサーバの値が変わらないと作り直されず、送った値のまま残って保存済みに見える
                void onSaveText({ [start.key]: next, [end.key]: next }).then((ok) => { if (!ok) input.value = server ?? ''; });
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
